import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Check, Flame, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function Daily({ couple, slot, me, partner }) {
  const [dq, setDq] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/couples/${couple.id}/daily-question`);
    setDq(data);
  };
  const loadHistory = async () => {
    const { data } = await api.get(`/couples/${couple.id}/daily-question/history`);
    setHistory(data);
  };

  useEffect(() => { load(); loadHistory(); }, [couple.id]); // eslint-disable-line

  const myAnswered = dq?.[`${slot}_answered`];

  const submit = async () => {
    if (!draft.trim()) return toast.error("Write something from the heart 💭");
    setBusy(true);
    try {
      const { data } = await api.post(`/couples/${couple.id}/daily-question/answer`, { slot, answer: draft });
      setDq(data);
      setDraft("");
      toast.success(data.revealed ? "Both answered — revealed! 💕" : "Answer saved. Waiting for your partner…");
      loadHistory();
    } catch {
      toast.error("Could not save your answer.");
    } finally {
      setBusy(false);
    }
  };

  if (!dq) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const myAnswerText = slot === "a" ? dq.answer_a : dq.answer_b;
  const partnerAnswerText = slot === "a" ? dq.answer_b : dq.answer_a;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-primary">Daily question</div>
          <h1 className="font-serif text-3xl font-semibold">One question a day</h1>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-accent px-3 py-1.5" data-testid="streak-badge">
          <Flame className="h-4 w-4 text-primary" />
          <span className="text-sm font-bold text-accent-foreground">{dq.streak}</span>
        </div>
      </div>

      <div className="rounded-3xl bg-gradient-to-br from-accent/60 to-card border border-border/70 p-6 shadow-sm">
        <p className="font-serif text-2xl font-medium leading-snug" data-testid="daily-question-text">{dq.question}</p>
      </div>

      {!myAnswered ? (
        <div className="space-y-3">
          <Textarea
            data-testid="answer-input" value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder="Your answer…" rows={4}
            className="rounded-2xl border-border bg-card resize-none text-base"
          />
          <Button data-testid="submit-answer-btn" disabled={busy} onClick={submit} className="w-full rounded-full h-12 font-semibold">
            {busy ? "Saving…" : "Share my answer"}
          </Button>
        </div>
      ) : dq.revealed ? (
        <motion.div initial={{ opacity: 0, rotateX: -25 }} animate={{ opacity: 1, rotateX: 0 }} className="space-y-3" data-testid="answers-revealed">
          <AnswerBubble name={me?.name} text={myAnswerText} mine />
          <AnswerBubble name={partner?.name} text={partnerAnswerText} />
        </motion.div>
      ) : (
        <div className="rounded-3xl border border-border/70 bg-card p-6 text-center" data-testid="waiting-partner">
          <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center mx-auto mb-3">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <p className="font-serif text-xl">Your answer is locked in ✨</p>
          <p className="text-sm text-muted-foreground mt-1">
            We'll reveal both answers once {partner?.name || "your partner"} responds.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-xs text-primary font-medium">
            <Check className="h-4 w-4" /> You answered today
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3 mt-2">Past answers</div>
          <div className="space-y-3">
            {history.slice(0, 15).map((h) => (
              <div key={h.date} className="rounded-2xl bg-card border border-border/60 p-4" data-testid="history-item">
                <div className="text-[11px] text-muted-foreground">{new Date(h.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                <p className="font-serif text-base font-medium mt-1">{h.question}</p>
                <div className="mt-2 space-y-1.5">
                  <p className="text-sm"><span className="text-primary font-semibold">{couple.partner_a?.name}: </span>{h.answer_a}</p>
                  <p className="text-sm"><span className="text-primary font-semibold">{couple.partner_b?.name}: </span>{h.answer_b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnswerBubble({ name, text, mine }) {
  return (
    <div className={`rounded-3xl p-5 border ${mine ? "bg-primary/10 border-primary/30" : "bg-card border-border/70"}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Heart className={`h-4 w-4 ${mine ? "text-primary fill-primary" : "text-muted-foreground"}`} />
        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{name}</span>
      </div>
      <p className="text-base leading-relaxed">{text}</p>
    </div>
  );
}
